import PropTypes from 'prop-types'
import { assocPath, pathOr, path, uniqBy, isNil } from 'ramda'

import ActiveRecord from 'model/activeRecord'
import MESSAGES_EVENTS from 'apollo/subscriptions/MESSAGES_EVENTS'
import User from 'model/person/user'
import ChatMessage from 'model/chats/chatMessage'
import { cache } from 'apollo/client'
import { initArray } from 'model/helpers'

class Chat extends ActiveRecord {
  constructor(init) {
    super(init)
    // console.log('> Chat constructor -', init)
    if (isNil(init)) return undefined
    this._archived = init.archived
    this._subject = init.subject
    this._inactive = init.inactive
    this._member_user = new User(init.member_user)
    this._navigators =
      init.navigators?.map(navigator => new User(navigator)) || []
    this._messages = initArray(init, ['messages', 'nodes'], ChatMessage)
    this._last_message_at = init.last_message_at
    this._count_require_navigator_response_messages =
      init.count_require_navigator_response_messages
  }

  static shape = {
    ...ActiveRecord.shape,
    archived: PropTypes.bool.isRequired,
    subject: PropTypes.string,
    inactive: PropTypes.bool.isRequired,
    member_user: PropTypes.instanceOf(User),
    navigators: PropTypes.arrayOf(PropTypes.instanceOf(User)),
    messages: PropTypes.arrayOf(PropTypes.instanceOf(ChatMessage)),
    count_require_navigator_response_messages: PropTypes.number,
    last_message_at: PropTypes.string
  }

  // Getters for class fields
  get archived() {
    return this._archived
  }

  get subject() {
    return this._subject
  }

  get inactive() {
    return this._inactive
  }

  get member_user() {
    return this._member_user
  }

  get navigators() {
    return this._navigators
  }

  get messages() {
    return this._messages || []
  }

  get count_require_navigator_response_messages() {
    return this._count_require_navigator_response_messages || null
  }

  get last_message_at() {
    return this._last_message_at || null
  }

  // Helpers
  subscribeChat(subscribeToMore) {
    // console.log('subscribeChat', this._id)
    if (subscribeToMore) {
      return subscribeToMore({
        document: MESSAGES_EVENTS,
        variables: { chat_id: this._id },
        updateQuery: (
          currentData,
          {
            subscriptionData: {
              data: {
                messages_events: { event_type, message }
              }
            }
          }
        ) => {
          let result = currentData

          const {
            chat: {
              messages: { nodes: messages }
            }
          } = currentData
          const clonedMessage = { ...message }

          // Apollo will automatically update cache with data, received in subscription
          // but adding and removing items to a cache should be done manually
          if (event_type === 'created') {
            result = assocPath(
              ['chat', 'messages', 'nodes'],
              uniqBy(path(['id']), [...messages, clonedMessage]),
              currentData
            )
          }

          if (event_type === 'deleted') {
            result = assocPath(
              ['chat', 'messages', 'nodes'],
              messages.filter(node => node.id !== clonedMessage.id),
              currentData
            )

            // Gotta invalidate the obsolete cache for deletion to work
            cache.evict({
              id: `Chat:${clonedMessage.chat_id}`,
              fieldName: 'messages'
            })
            cache.gc()
          }

          return result
        }
      })
    }
    return () => {}
  }

  static fetchMessages(data, fetchMore) {
    // console.warn('fetchMessages', data)
    const dataPath = ['chat', 'messages']
    const hasPreviousPage = pathOr(
      false,
      [...dataPath, 'pageInfo', 'hasPreviousPage'],
      data
    )

    if (!hasPreviousPage) return

    const startCursor = pathOr(
      '',
      [...dataPath, 'pageInfo', 'startCursor'],
      data
    )

    fetchMore({
      variables: {
        cursor: startCursor
      }
    })
  }

  static async fetchAllMessages(
    initialMessages,
    fetchMore,
    last,
    allMessages = []
  ) {
    const messages = initialMessages?.nodes || []
    const composedMessages = [...messages, ...allMessages]

    const hasPreviousPage = initialMessages?.pageInfo?.hasPreviousPage
    const startCursor = initialMessages?.pageInfo?.startCursor

    if (!hasPreviousPage) return composedMessages

    const { data } = await fetchMore({
      variables: { last, cursor: startCursor }
    })

    return this.fetchAllMessages(
      data?.chat?.messages,
      fetchMore,
      last,
      composedMessages
    )
  }
}

export default Chat
